import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { t, getal, wanneer, geleden } from '../i18n.js';
import { St, bronstatus } from '../components/Status.jsx';
import Gemaskeerd from '../components/Gemaskeerd.jsx';

/**
 * De pagina **Identity** (Cisco ISE, docs/ontwerp/ise.md): wie er op het netwerk zit, te zoeken op adres, MAC-adres,
 * gebruiker of computer (ook wie er eerder zat: sessies blijven 90 dagen), mislukte logins en beheer van de laatste
 * week, en de ISE-nodes met hun stand.
 */
export default function Identity({ klant }) {
  const [tekst, zetTekst] = useState('');
  const [zoek, zetZoek] = useState('');
  /* Zoeken als je even stopt met typen, niet bij elke toets. */
  useEffect(() => { const h = setTimeout(() => zetZoek(tekst.trim()), 300); return () => clearTimeout(h); }, [tekst]);
  const q = useQuery({ queryKey: ['identity', klant, zoek], queryFn: () => api.identity(klant, zoek), refetchInterval: 30_000 });

  const kop = (
    <div className="page-head"><div>
      <h1>Identity</h1>
      <p className="sub">{t('Wie er op het netwerk zit, volgens Cisco ISE')}</p>
    </div></div>
  );
  if (q.isPending) return <>{kop}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}<div className="melder rood">{t(q.error.message)}</div></>;
  const { nodes, counts, sessions, failed, admin } = q.data.data;

  if (!nodes.length) {
    return <>{kop}<section className="card"><p className="empty">{t('Nog geen ISE in ADTS ZERO. Voeg elke ISE-node toe onder Bronnen › Bron toevoegen › Cisco ISE; de stappen voor ISE staan daarna bij de bron.')}</p>
      <div className="actions"><Link className="btn" to="/bronnen/nieuw">{t('Bron toevoegen')}</Link></div></section></>;
  }
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{getal(n)}</div><div className="t">{label}</div></div>;
  const waar = (s) => (s.nas.ssid ? t('wifi {ssid}', { ssid: s.nas.ssid }) : [s.nas.name ?? s.nas.ip, s.nas.port].filter(Boolean).join(' · '));

  return (
    <>
      {kop}
      <div className="tiles">
        {tegel(counts.open, t('apparaten op het netwerk'))}
        {tegel(counts.mislukt, t('mislukte logins in 24 uur'), counts.mislukt ? 'warn' : '')}
        {tegel(counts.namen, t('gebruikers met een mislukte login'))}
        {tegel(counts.beheer, t('beheerhandelingen in 24 uur'))}
      </div>

      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Sessies')}</h2>
          <input type="search" className="search" value={tekst} onChange={(e) => zetTekst(e.target.value)} maxLength={100}
            placeholder={t('Zoek op adres, MAC-adres, gebruiker of computer')} aria-label={t('Zoek op adres, MAC-adres, gebruiker of computer')} /></header>
        {!sessions.length ? <p className="empty">{zoek ? t('Niets gevonden voor {zoek}.', { zoek }) : t('Nog geen sessies uit ISE.')}</p> : (
          <table>
            <thead><tr><th>{t('Adres')}</th><th>{t('Gebruiker of computer')}</th><th className="hide-sm">{t('MAC-adres')}</th>
              <th className="hide-sm">{t('Aangesloten op')}</th><th className="hide-sm">{t('Toegang')}</th><th>{t('Sessie')}</th></tr></thead>
            <tbody>{sessions.map((s, i) => (
              <tr key={i}>
                <td className="mono">{s.ip ?? '—'}</td>
                <td>{s.user ? <Gemaskeerd veld={s.user} /> : s.hostname ? <Gemaskeerd veld={s.hostname} /> : <span className="muted">{t('geen (MAB)')}</span>}{s.user && s.hostname && <span className="muted"> · <Gemaskeerd veld={s.hostname} /></span>}</td>
                <td className="mono hide-sm">{s.mac ? <Gemaskeerd veld={s.mac} mono /> : s.clientIp ? <span className="muted">{t('VPN vanaf')} <Gemaskeerd veld={s.clientIp} mono /></span> : '—'}{s.profile && <><br /><span className="muted">{s.profile}</span></>}</td>
                <td className="hide-sm">{waar(s) || '—'}</td>
                <td className="hide-sm">{s.authz ?? '—'}{s.method && <span className="muted"> · {s.method}</span>}</td>
                <td>{s.live ? <span className="muted">{t('nu, gevraagd aan ISE')}</span> : s.endedAt ? <span className="muted">{wanneer(s.startedAt)} – {wanneer(s.endedAt)}</span>
                  : <>{t('sinds {tijd}', { tijd: wanneer(s.startedAt) })}<br /><span className="muted">{t('laatst {tijd}', { tijd: geleden(s.lastAt) })}</span></>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </section>

      <div className="grid-2" style={{ marginBottom: 18 }}>
        <section className="card">
          <header><h2>{t('Mislukte logins')}</h2><span className="meta">{t('laatste 7 dagen')}</span></header>
          {!failed.length ? <p className="empty">{t('Geen mislukte logins.')}</p> : (
            <div className="rows">{failed.map((e, i) => (
              <div className="row" key={i} style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
                <span className="t">{wanneer(e.at)}</span>
                <span><b className="mono"><Gemaskeerd veld={e.user} mono /></b><br /><span className="soft" style={{ overflowWrap: 'anywhere' }}><Gemaskeerd veld={e.text} /></span></span>
              </div>
            ))}</div>
          )}
        </section>
        <section className="card">
          <header><h2>{t('Beheer')}</h2><span className="meta">{t('ISE en TACACS+, laatste 7 dagen')}</span></header>
          {!admin.length ? <p className="empty">{t('Geen beheer.')}</p> : (
            <div className="rows">{admin.map((e, i) => (
              <div className="row" key={i} style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
                <span className="t">{wanneer(e.at)}</span>
                <span><b className="mono"><Gemaskeerd veld={e.user} mono /></b>{e.src && <span className="muted mono"> · <Gemaskeerd veld={e.src} mono /></span>}<br />
                  <span className="soft" style={{ overflowWrap: 'anywhere' }}><Gemaskeerd veld={e.text} /></span></span>
              </div>
            ))}</div>
          )}
        </section>
      </div>

      <section className="card">
        <header><h2>{t('ISE-nodes')}</h2></header>
        <table>
          <thead><tr><th>{t('Node')}</th><th>{t('Adres')}</th><th>{t('Stand')}</th><th>{t('Laatste bericht')}</th></tr></thead>
          <tbody>{nodes.map((n) => (
            <tr key={n.id}>
              <td><Link to={`/bronnen/${n.id}`}>{n.name}</Link>{n.tacacs && <span className="tag" style={{ marginLeft: 6 }}>TACACS+</span>}</td>
              <td className="mono">{n.ip}</td>
              <td><St soort={bronstatus(n).soort}>{bronstatus(n).label}</St></td>
              <td>{geleden(n.lastMessageAt)}</td>
            </tr>
          ))}</tbody>
        </table>
      </section>
    </>
  );
}
